import { IssueEntity, IssueState } from '@/types';
import {
    CLEAR_PINNED_ISSUE,
    ClearPinnedIssueAction,
    FETCH_ISSUES_SUCCESS,
    FetchIssuesSuccessAction,
    ISSUE_FETCHED,
    ISSUE_UPDATED,
    IssueFetchedAction,
    IssueUpdatedAction,
    PIN_CREATED_ISSUE,
    PinCreatedIssueAction,
} from '@/actions/triage';

const CLOSED_ISSUE_STATES = [
    IssueState.FixMerged,
    IssueState.FixRejected,
    IssueState.NotAPriority,
    IssueState.ResolvedExternally,
];

// Open/closed bucket a state belongs to — drives count adjustments and tab membership below.
const issueStatusGroup = (state: IssueState): 'open' | 'closed' =>
    CLOSED_ISSUE_STATES.includes(state) ? 'closed' : 'open';

export interface IssuesStateForApp {
    issues: IssueEntity[];
    openCount: number;
    closedCount: number;
    defaultStateSortOrder?: IssueState[];
    // Deep-linked issues fetched on demand (may not belong to the active tab/list)
    coldLoadedById?: Record<string, IssueEntity>;
    pinnedIssue?: IssueEntity;
}

export type IssuesByAppId = Record<string, IssuesStateForApp>;

const emptyForApp: IssuesStateForApp = { issues: [], openCount: 0, closedCount: 0 };

// Hoists the pinned issue to the top of the list, preferring the freshest copy present in the list.
const applyPin = (
    issues: IssueEntity[],
    pinned?: IssueEntity,
): { issues: IssueEntity[]; pinnedIssue?: IssueEntity } => {
    if (!pinned) {
        return { issues, pinnedIssue: undefined };
    }
    const fresh = issues.find((i) => i.id === pinned.id) ?? pinned;
    return { issues: [fresh, ...issues.filter((i) => i.id !== pinned.id)], pinnedIssue: fresh };
};

type IssuesAction =
    | FetchIssuesSuccessAction
    | IssueUpdatedAction
    | IssueFetchedAction
    | PinCreatedIssueAction
    | ClearPinnedIssueAction;

export function issuesReducer(state: IssuesByAppId = {}, action: IssuesAction): IssuesByAppId {
    switch (action.type) {
        case FETCH_ISSUES_SUCCESS: {
            const { appId, issues, openCount, closedCount, defaultStateSortOrder } = action.payload;
            // Preserve any cold-loaded (deep-linked) issues across list (re)fetches so the route-driven drawer
            // doesn't lose its issue when the list finishes loading.
            const coldLoadedById = state[appId]?.coldLoadedById;
            // The pin rides the page the issue was created from; navigating away clears it (see the list's
            // page handler), so it never follows the user around.
            const pinned = applyPin(issues, state[appId]?.pinnedIssue);
            return {
                ...state,
                [appId]: {
                    issues: pinned.issues,
                    openCount,
                    closedCount,
                    defaultStateSortOrder,
                    coldLoadedById,
                    pinnedIssue: pinned.pinnedIssue,
                },
            };
        }
        case PIN_CREATED_ISSUE: {
            const { appId, issue } = action.payload;
            const existing = state[appId] ?? emptyForApp;
            // Drop any existing copy and prepend so the new issue shows on top immediately (before the refetch).
            const issues = [issue, ...existing.issues.filter((i) => i.id !== issue.id)];
            return { ...state, [appId]: { ...existing, issues, pinnedIssue: issue } };
        }
        // Dispatched by the list on any filter/sort change and on mount; the follow-up fetch then places it naturally.
        case CLEAR_PINNED_ISSUE: {
            const { appId } = action.payload;
            const existing = state[appId];
            if (!existing?.pinnedIssue) {
                return state;
            }
            return { ...state, [appId]: { ...existing, pinnedIssue: undefined } };
        }
        case ISSUE_UPDATED: {
            const { appId, issue, statusGroup } = action.payload;
            const existing = state[appId] ?? emptyForApp;
            const previous = existing.issues.find((i) => i.id === issue.id);

            const leavingTab = !!statusGroup && issueStatusGroup(issue.state) !== statusGroup;
            const deferRemoval = leavingTab && issue.state === IssueState.NotAPriority;

            // Adjust open/closed totals when the state change crosses buckets (skip while deferring the move).
            let { openCount, closedCount } = existing;
            if (previous && !deferRemoval) {
                const from = issueStatusGroup(previous.state);
                const to = issueStatusGroup(issue.state);
                if (from !== to) {
                    if (from === 'open') openCount = Math.max(0, openCount - 1);
                    else closedCount = Math.max(0, closedCount - 1);
                    if (to === 'open') openCount += 1;
                    else closedCount += 1;
                }
            }

            // Drop the issue when it leaves the active tab; keep it (faded) in place otherwise or while deferring.
            const issues =
                leavingTab && !deferRemoval ?
                    existing.issues.filter((i) => i.id !== issue.id)
                :   existing.issues.map((i) => (i.id === issue.id ? issue : i));

            // Keep a cold-loaded copy (if any) fresh too, so the route-driven drawer reflects the update.
            const coldLoadedById =
                existing.coldLoadedById?.[issue.id] ?
                    { ...existing.coldLoadedById, [issue.id]: issue }
                :   existing.coldLoadedById;

            // Keep the pin fresh; drop it if the pinned issue just left the active tab.
            let pinnedIssue = existing.pinnedIssue;
            if (pinnedIssue?.id === issue.id) {
                pinnedIssue = leavingTab && !deferRemoval ? undefined : issue;
            }

            return { ...state, [appId]: { ...existing, issues, openCount, closedCount, coldLoadedById, pinnedIssue } };
        }
        case ISSUE_FETCHED: {
            const { appId, issue } = action.payload;
            const existing = state[appId] ?? emptyForApp;
            const inList = existing.issues.find((i) => i.id === issue.id);
            return {
                ...state,
                [appId]: {
                    ...existing,
                    issues: inList ? existing.issues.map((i) => (i.id === issue.id ? issue : i)) : existing.issues,
                    coldLoadedById: { ...(existing.coldLoadedById ?? {}), [issue.id]: issue },
                },
            };
        }
        default:
            return state;
    }
}

export default issuesReducer;
